import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import {
    Box,
    Collapse,
    List,
    ListItemText,
    Popover,
    Tooltip
} from '@mui/material';
//
import { StyledNavItem, StyledNavItemIcon } from './styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from '../../i18n';

/** True when this branch of the menu contains the route currently displayed. */
const containsPath = (items = [], pathname) =>
    items.some(
        (child) =>
            child.path === pathname || containsPath(child.sublistData, pathname)
    );

// ----------------------------------------------------------------------

NavSection.propTypes = {
    data: PropTypes.array
};

export default function NavSection({ data = [], collapsed = false, ...other }) {
    const [selectedItem, setSelectedItem] = useState(null);

    return (
        <Box {...other}>
            <List disablePadding sx={{ p: 1 }}>
                {data.map((item) => (
                    <ListItem
                        key={item.title}
                        item={item}
                        collapsed={collapsed}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                    />
                ))}
            </List>
        </Box>
    );
}

// ----------------------------------------------------------------------
const NestedList = ({ data = [], pl = 4, mrItem, onClosePopover }) => {
    return (
        <List
            component="div"
            disablePadding
            sx={{
                pl: pl,
                // Children were indented but otherwise unattached to the group
                // they belong to. A rule down the left reads as grouping without
                // repeating the group's name as a caption above it.
                ...(pl
                    ? {
                        ml: 2,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        pl: pl - 2
                    }
                    : {})
            }}
        >
            {data.map((item) => (
                <ListItem
                    key={item.title}
                    item={item}
                    mr={mrItem}
                    isNested={true}
                    onClosePopover={onClosePopover}
                />
            ))}
        </List>
    );
};
//----------------------------------------------------------------------

const ListItem = ({
    item,
    mr = 0,
    isNested = false,
    collapsed = false,
    onClosePopover
}) => {
    const { title, path, icon, info, sublistData } = item;
    const { pathname } = useLocation();

    // A group started collapsed regardless of where you were, so landing on a
    // nested page directly — a bookmark, a reload, the command palette — left
    // the sidebar with nothing highlighted at all.
    const hasActiveChild = useMemo(
        () => containsPath(sublistData, pathname),
        [sublistData, pathname]
    );

    const [openCollapse, setOpenCollapse] = useState(hasActiveChild);
    const [openPopover, setOpenPopover] = useState(false);
    const itemRef = useRef(null);
    function usePrevious(value) {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        }, [value]);
        return ref.current;
    }
    const prevCollapsed = usePrevious(collapsed);

    useEffect(() => {
        if (collapsed && !prevCollapsed) {
            setOpenPopover(false);
        }
    }, [collapsed, prevCollapsed]);

    useEffect(() => {
        if (hasActiveChild) setOpenCollapse(true);
    }, [hasActiveChild]);

    // A thirty-page menu puts many pages below the fold: bring the current one
    // into view, after its group has had time to expand.
    const isCurrent = !sublistData && path === pathname;
    useEffect(() => {
        if (!isCurrent) return undefined;
        const timer = setTimeout(() => {
            itemRef.current?.scrollIntoView?.({ block: 'nearest' });
        }, 300);
        return () => clearTimeout(timer);
    }, [isCurrent]);

    const translatedTitle = i18n.t(title);

    const handleClick = () => {
        if (sublistData) {
            if (collapsed) {
                setOpenPopover(!openPopover);
            } else {
                setOpenCollapse(!openCollapse);
            }
        }
        onClosePopover?.();
    };

    const ListItemComponent = sublistData ? 'div' : RouterLink;
    const listItemProps = sublistData ? {} : { to: path };

    const content = (
        <StyledNavItem
            component={ListItemComponent}
            onClick={handleClick}
            // Groups aren't routes, so NavLink never marks them active. When a
            // group is closed over the active page, or the sidebar is collapsed
            // to icons (its children are not shown even if it is "open"),
            // colour the group itself so the sidebar still says where you are.
            sx={
                sublistData && hasActiveChild && (collapsed || !openCollapse)
                    ? { color: 'primary.main', bgcolor: 'primary.tint6' }
                    : undefined
            }
            aria-expanded={
                sublistData ? (collapsed ? openPopover : openCollapse) : undefined
            }
            {...listItemProps}
        >
            <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>

            {!collapsed && (
                <ListItemText
                    disableTypography
                    primary={translatedTitle}
                    sx={{ ml: 1, mr: mr }}
                />
            )}

            {!collapsed && info}
        </StyledNavItem>
    );
    return (
        <>
            <Box ref={itemRef}>
                {collapsed ? (
                    <Tooltip title={translatedTitle} placement="right" arrow>
                        {content}
                    </Tooltip>
                ) : (
                    content
                )}
            </Box>

            {sublistData &&
                (!collapsed ? (
                    <Collapse in={openCollapse} timeout="auto" unmountOnExit>
                        <NestedList data={sublistData} />
                    </Collapse>
                ) : (
                    <Popover
                        open={Boolean(openPopover && itemRef.current)}
                        anchorEl={openPopover ? itemRef.current : null}
                        onClose={() => setOpenPopover(false)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    p: 1,
                                    minWidth: 180,
                                    // Clear of the rail: flush against the
                                    // item, its shadow fell over the icon.
                                    ml: 1.5
                                }
                            }
                        }}
                    >
                        <Box>
                            <NestedList
                                data={sublistData}
                                pl={0}
                                mrItem={2}
                                onClosePopover={() => setOpenPopover(false)}
                            />
                        </Box>
                    </Popover>
                ))}
        </>
    );
};

ListItem.propTypes = {
    item: PropTypes.object.isRequired,
    isNested: PropTypes.bool
};
